import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useStory } from "@/hooks/use-baseline";
import { cn } from "@/lib/utils";

export function StoryDetailSheet() {
  const { solutionId, storyId } = useParams({ from: "/s/$solutionId/scope/$storyId" });
  const navigate = useNavigate();
  const { data, isLoading } = useStory(storyId);

  function close() {
    navigate({ to: "/s/$solutionId/scope", params: { solutionId } });
  }

  return (
    <Sheet open onOpenChange={(open) => !open && close()}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto">
        {isLoading && (
          <div className="p-6 space-y-3">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-32 w-full mt-4" />
          </div>
        )}
        {data && (
          <>
            <SheetHeader className="border-b border-[var(--color-border)]">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                {data.deliverableCode} · {data.deliverableTitle} · {data.epicCode}
              </div>
              <SheetTitle className="font-display text-lg leading-tight mt-1">
                <span className="font-mono text-sm text-muted-foreground mr-2">{data.code}</span>
                {data.label?.trim() || data.text}
              </SheetTitle>
              {data.label?.trim() && (
                <SheetDescription className="mt-1 text-foreground/80">{data.text}</SheetDescription>
              )}
            </SheetHeader>

            <div className="p-6 space-y-6">
              <section>
                <h3 className="font-display text-sm font-semibold mb-2">Acceptance criteria</h3>
                {data.acceptanceCriteria.length > 0 ? (
                  <ol className="space-y-2">
                    {data.acceptanceCriteria.map((ac, i) => (
                      <li
                        key={ac.id}
                        className="flex gap-3 rounded-md border border-[var(--color-border)] p-3"
                      >
                        <span className="text-xs font-mono text-muted-foreground tabular-nums w-6 shrink-0">
                          {i + 1}.
                        </span>
                        <span className="text-sm text-foreground/85">{ac.text}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className="rounded-md border border-dashed border-[var(--color-border)] p-4 text-sm text-muted-foreground">
                    No acceptance criteria yet.
                  </div>
                )}
              </section>

              <section>
                <h3 className="font-display text-sm font-semibold mb-2">
                  Matched triage items
                  {data.matchedItems.length > 0 && (
                    <span className="ml-2 text-xs text-muted-foreground font-normal">
                      ({data.matchedItems.length})
                    </span>
                  )}
                </h3>
                {data.matchedItems.length > 0 ? (
                  <ul className="space-y-2">
                    {data.matchedItems.map((m) => (
                      <li key={m.id}>
                        <Link
                          to="/s/$solutionId/review/$itemId"
                          params={{ solutionId, itemId: m.id }}
                          className="flex items-start gap-3 rounded-md border border-[var(--color-border)] p-3 hover:border-nymbl-blue hover:shadow-sm transition-colors"
                        >
                          <span className="font-mono text-[11px] text-muted-foreground tabular-nums w-16 shrink-0">
                            {m.externalKey ?? m.id.slice(0, 6)}
                          </span>
                          <span className="text-sm text-foreground flex-1 line-clamp-2">
                            {m.extracted}
                          </span>
                          {m.overlapPct > 0 && (
                            <span
                              className={cn(
                                "text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded shrink-0",
                                m.overlapPct >= 80
                                  ? "bg-success-bg text-success"
                                  : m.overlapPct >= 50
                                    ? "bg-info-bg text-info"
                                    : "bg-muted text-foreground/60",
                              )}
                            >
                              {m.overlapPct}%
                            </span>
                          )}
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="rounded-md border border-dashed border-[var(--color-border)] p-4 text-sm text-muted-foreground">
                    No items mapped to this story yet.
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
